import { filetypeinfo } from 'magic-bytes.js';

export const MULTIPART_THRESHOLD = 32 * 1024 * 1024;
export const MAX_FILE_SIZE_UNVERIFIED = 2 * 1024 * 1024 * 1024;
export const MAX_FILE_SIZE_VERIFIED = 10 * 1024 * 1024 * 1024;
export const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'application/octet-stream',
];

export const shouldUseMultipart = (size: number) => size >= MULTIPART_THRESHOLD;

const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v)$/i;

export const isVideoFile = (file: File): boolean => {
  if (file.type && file.type.startsWith('video/')) return true;
  if (ALLOWED_VIDEO_TYPES.includes(file.type)) return true;
  return VIDEO_EXTENSIONS.test(file.name);
};

const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  '.mp4': 'video/mp4',
  '.m4v': 'video/mp4',
  '.mov': 'video/quicktime',
};

export const getVideoContentType = (file: File): string => {
  if (file.type && ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return file.type;
  }
  const ext = file.name.match(/\.\w+$/)?.[0]?.toLowerCase();
  if (ext && EXTENSION_TO_CONTENT_TYPE[ext]) {
    return EXTENSION_TO_CONTENT_TYPE[ext];
  }
  return 'application/octet-stream';
};

export type SniffResult =
  | { kind: 'video'; contentType: string }
  | { kind: 'image' }
  | { kind: 'video_unsupported' }
  | { kind: 'other' }
  | { kind: 'unknown' };

const readFileBytes = (file: File, length: number): Promise<Uint8Array | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file.slice(0, length));
  });

export const sniffFile = async (file: File): Promise<SniffResult> => {
  const bytes = await readFileBytes(file, 64);
  if (!bytes || !bytes.length) {
    return { kind: 'unknown' };
  }

  const guesses = filetypeinfo(bytes).filter((guess) => guess.mime);
  if (!guesses.length) {
    return { kind: 'unknown' };
  }

  for (const guess of guesses) {
    const mime = (guess.mime || '').toLowerCase();
    if (mime.includes('quicktime')) {
      return { kind: 'video', contentType: 'video/quicktime' };
    }
    if (mime.includes('mp4') || mime.includes('m4v')) {
      return { kind: 'video', contentType: 'video/mp4' };
    }
  }
  if (guesses.some((guess) => guess.mime!.startsWith('image/'))) {
    return { kind: 'image' };
  }
  if (guesses.some((guess) => guess.mime!.startsWith('video/'))) {
    return { kind: 'video_unsupported' };
  }
  return { kind: 'other' };
};

export const detectVideoContentType = async (file: File): Promise<string> => {
  const sniffed = await sniffFile(file);
  if (sniffed.kind === 'video') {
    return sniffed.contentType;
  }
  return getVideoContentType(file);
};
